// Application shell: sidebar on desktop; on phones a bottom tab bar and a
// compact top bar with a back button, like a native app. Plus the persistent
// impersonation banner, the install affordance and silent updates.

import { useEffect, useRef, type ReactNode } from 'react';
import {
  AppShell,
  Button,
  Divider,
  Group,
  Menu,
  NavLink,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link, Outlet, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import {
  IconChartLine,
  IconChevronLeft,
  IconFolders,
  IconCpu,
  IconLogout,
  IconShieldLock,
  IconUser,
} from '@tabler/icons-react';
import { useTranslation } from '@/i18n/I18nProvider';
import { useSession } from '@/session/SessionProvider';
import { AppearanceControl, LanguageControl } from '@/components/controls';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { Logo } from '@/components/Logo';
import { UpdatePrompt } from '@/components/UpdatePrompt';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  adminOnly?: boolean;
  /**
   * Additional path prefixes belonging to this destination.
   *
   * Device setup and configuration are reached from the Sources screen and are
   * part of managing sources, but live under `/devices`. Without this the
   * navigation highlights nothing at all while the reader is in that flow,
   * which looks like the destination was lost.
   */
  alsoOwns?: readonly string[];
}

/**
 * Navigation is split in two.
 *
 * The data comes first: opening the app should land on what the sensors are
 * reporting. The source inventory and administration are management tasks done
 * occasionally rather than monitored continuously, so they sit below a labelled
 * divider instead of competing with the charts for attention. Hiding them
 * entirely would make a rare task hard to find; demoting them keeps the common
 * case first without adding a click to the rare one.
 *
 * Creating a feed is deliberately **not** here. It is an action on the Sources
 * screen, where the button already sits, and an action does not belong in a list
 * of destinations: it highlighted at the same time as its own parent, because
 * `/sources/new` is also a child of `/sources`.
 */
function usePrimaryNavItems(): NavItem[] {
  const t = useTranslation();
  return [
    { to: '/feeds', label: t('nav.dashboard'), icon: <IconChartLine size={18} aria-hidden /> },
    { to: '/groups', label: t('nav.groups'), icon: <IconFolders size={18} aria-hidden /> },
  ];
}

function useManageNavItems(): NavItem[] {
  const t = useTranslation();
  return [
    {
      to: '/sources',
      label: t('nav.sources'),
      icon: <IconCpu size={18} aria-hidden />,
      // Adding a device and editing its configuration are reached from the
      // Sources screen and belong to the same destination.
      alsoOwns: ['/devices'],
    },
    {
      to: '/admin',
      label: t('nav.admin'),
      icon: <IconShieldLock size={18} aria-hidden />,
      adminOnly: true,
    },
    { to: '/account', label: t('nav.account'), icon: <IconUser size={18} aria-hidden /> },
  ];
}

function NavSection({
  items,
  onNavigate,
  currentPath,
  label,
}: {
  items: NavItem[];
  onNavigate?: () => void;
  currentPath: string;
  label: string;
}): ReactNode {
  // Only the most specific matching destination is highlighted. Without this a
  // parent and its child both claim the current page, which reads as two active
  // items and leaves the reader unsure where they are.
  const activeTo = items
    .filter((item) =>
      [item.to, ...(item.alsoOwns ?? [])].some(
        (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`),
      ),
    )
    .sort((first, second) => second.to.length - first.to.length)[0]?.to;

  return (
    <nav aria-label={label}>
      <Stack gap={4}>
        {items.map((item) => {
          const isActive = item.to === activeTo;
          return (
            <NavLink
              key={item.to}
              component={Link}
              to={item.to}
              label={item.label}
              leftSection={item.icon}
              active={isActive}
              {...(isActive ? { 'aria-current': 'page' as const } : {})}
              {...(onNavigate ? { onClick: () => onNavigate() } : {})}
            />
          );
        })}
      </Stack>
    </nav>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }): ReactNode {
  const t = useTranslation();
  const { isAdmin, isImpersonating } = useSession();
  const primary = usePrimaryNavItems();
  const manage = useManageNavItems();
  const currentPath = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();

  const visibleManage = manage.filter((item) => !item.adminOnly || (isAdmin && !isImpersonating));

  return (
    <Stack gap="sm" h="100%" justify="space-between">
      <Stack gap="xs">
        <Group gap="xs" px="sm" pt="sm" wrap="nowrap">
          <Logo size={30} />
          <Text fw={700} fz="xl" className="sensing-hand">
            Sensing
          </Text>
        </Group>
        <Text size="xs" c="dimmed" px="sm">
          {t('app.tagline')}
        </Text>
        <Divider my="xs" color="var(--sensing-border)" />
        <NavSection
          items={primary}
          currentPath={currentPath}
          label={t('nav.primaryNavigation')}
          {...(onNavigate ? { onNavigate } : {})}
        />
        <Divider
          my="xs"
          color="var(--sensing-border)"
          label={t('nav.manage')}
          labelPosition="left"
        />
        <NavSection
          items={visibleManage}
          currentPath={currentPath}
          label={t('nav.manageNavigation')}
          {...(onNavigate ? { onNavigate } : {})}
        />
      </Stack>

      <Stack gap="sm" px="sm" pb="sm">
        <Stack gap={6}>
          <LanguageControl />
          <AppearanceControl />
        </Stack>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconLogout size={14} aria-hidden />}
          onClick={() => {
            onNavigate?.();
            void navigate({ to: '/account' });
          }}
        >
          {t('nav.signOut')}
        </Button>
      </Stack>
    </Stack>
  );
}

function AccountMenu(): ReactNode {
  const t = useTranslation();
  const { state } = useSession();
  const navigate = useNavigate();
  const session = state.status === 'authenticated' ? state.session : null;

  return (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <UnstyledButton aria-label={t('nav.account')}>
          <Group gap={6}>
            <IconUser size={18} aria-hidden />
            <Text size="sm">
              {session
                ? t(`account.role${session.role === 'admin' ? 'Admin' : 'Owner'}`)
                : t('nav.account')}
            </Text>
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconUser size={16} aria-hidden />}
          onClick={() => void navigate({ to: '/account' })}
        >
          {t('nav.account')}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLogout size={16} aria-hidden />}
          onClick={() => void navigate({ to: '/account' })}
        >
          {t('nav.signOut')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** Phones get a bottom tab bar and a compact top bar instead of the sidebar. */
const MOBILE_QUERY = '(max-width: 47.99em)';

/** Top-level destinations: the bottom tab bar on phones. */
function useTabs(): Array<{ to: string; label: string; icon: ReactNode; owns: string[] }> {
  const t = useTranslation();
  const { isAdmin, isImpersonating } = useSession();
  const tabs = [
    {
      to: '/feeds',
      label: t('nav.tabs.feeds'),
      icon: <IconChartLine size={22} aria-hidden />,
      owns: [],
    },
    {
      to: '/groups',
      label: t('nav.tabs.groups'),
      icon: <IconFolders size={22} aria-hidden />,
      owns: [],
    },
    {
      to: '/sources',
      label: t('nav.tabs.sources'),
      icon: <IconCpu size={22} aria-hidden />,
      owns: ['/devices'],
    },
  ];
  if (isAdmin && !isImpersonating) {
    tabs.push({
      to: '/admin',
      label: t('nav.tabs.admin'),
      icon: <IconShieldLock size={22} aria-hidden />,
      owns: [],
    });
  }
  tabs.push({
    to: '/account',
    label: t('nav.tabs.account'),
    icon: <IconUser size={22} aria-hidden />,
    owns: [],
  });
  return tabs;
}

function owningTab(tabs: ReturnType<typeof useTabs>, path: string) {
  return tabs.find((tab) =>
    [tab.to, ...tab.owns].some((prefix) => path === prefix || path.startsWith(`${prefix}/`)),
  );
}

/** The bottom tab bar: large targets, the current tab marked, above the home indicator. */
function TabBar(): ReactNode {
  const t = useTranslation();
  const tabs = useTabs();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const active = owningTab(tabs, path)?.to;
  return (
    <nav aria-label={t('nav.primaryNavigation')} className="sensing-tabbar">
      {tabs.map((tab) => {
        const isActive = tab.to === active;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className="sensing-tabbar-item"
            data-active={isActive || undefined}
            {...(isActive ? { 'aria-current': 'page' as const } : {})}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * The phone's top bar: the wordmark on a tab's own screen, a back button on
 * anything deeper, the way native apps behave.
 */
function MobileHeader(): ReactNode {
  const t = useTranslation();
  const tabs = useTabs();
  const router = useRouter();
  const navigate = useNavigate();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const tab = owningTab(tabs, path);
  const isTabRoot = tabs.some((item) => item.to === path);
  return (
    <Group h="100%" px="xs" wrap="nowrap" className="sensing-mobile-header">
      {isTabRoot || !tab ? (
        <Group gap={6} wrap="nowrap" px="xs">
          <Logo size={26} />
          <Text fw={700} className="sensing-hand" fz="xl">
            Sensing
          </Text>
        </Group>
      ) : (
        <UnstyledButton
          className="sensing-back"
          aria-label={t('nav.back')}
          onClick={() => {
            // Back through history when there is any inside the app;
            // otherwise up to the tab this screen belongs to.
            if (window.history.length > 1) {
              router.history.back();
            } else {
              void navigate({ to: tab.to });
            }
          }}
        >
          <IconChevronLeft size={24} aria-hidden />
          <span>{t('nav.back')}</span>
        </UnstyledButton>
      )}
    </Group>
  );
}

/** The authenticated layout: shell chrome wrapping every private route. */
export function AppShellLayout(): ReactNode {
  const t = useTranslation();
  const mobile = useMediaQuery(MOBILE_QUERY, false, { getInitialValueInEffect: false });
  const path = useRouterState({ select: (state) => state.location.pathname });
  const mainRef = useRef<HTMLElement | null>(null);

  // On a phone the frame is fixed like a native app's: the page never
  // scrolls, only the content between the top bar and the tab bar does, and
  // only when it is taller than that space. Pinning it with `position: fixed`
  // rather than a viewport-height unit matters on iOS, where an installed web
  // app's `100dvh` can exceed the visible area and leave a short page
  // scrollable. See `.sensing-app-frame` in global.css.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('sensing-app-frame', mobile);
    return () => root.classList.remove('sensing-app-frame');
  }, [mobile]);

  // The content area is the scroller on a phone, so a new screen starts at
  // its top (the router resets only the window).
  useEffect(() => {
    if (mobile) mainRef.current?.scrollTo({ top: 0 });
  }, [path, mobile]);

  return (
    <AppShell
      // The sidebar is not rendered on phones at all: hidden but present it
      // would be a second, invisible navigation for screen readers.
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: true, desktop: mobile } }}
      // For the footer Mantine adds the bottom inset to the height and pads by
      // it, so the height here is the tab bar alone. The padded strip under
      // the tabs is the footer's own box, which is why the footer (not just
      // the tab bar) carries the bar colour; otherwise the strip shows the
      // page colour as a gap above the home indicator.
      header={{ height: mobile ? 'calc(3.25rem + env(safe-area-inset-top))' : 60 }}
      footer={{ height: '3.75rem', collapsed: !mobile }}
      padding="md"
    >
      <AppShell.Header>
        {mobile ? (
          <MobileHeader />
        ) : (
          <Group h="100%" px="md" justify="space-between" wrap="nowrap">
            <Group gap={8} wrap="nowrap">
              <Logo size={28} />
              <Text fw={700} className="sensing-hand" fz="lg">
                Sensing
              </Text>
            </Group>
            <AccountMenu />
          </Group>
        )}
      </AppShell.Header>

      {mobile ? null : (
        <AppShell.Navbar p="xs">
          <SidebarContent />
        </AppShell.Navbar>
      )}

      <AppShell.Footer className="sensing-tabbar-footer">
        {mobile ? <TabBar /> : null}
      </AppShell.Footer>

      <AppShell.Main ref={mainRef} className={mobile ? 'sensing-mobile-main' : undefined}>
        <a className="sensing-skip-link" href="#main-content">
          {t('a11y.skipToContent')}
        </a>
        <ImpersonationBanner />
        <UpdatePrompt />
        <div id="main-content">
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  );
}
